/* eslint-disable max-lines */

import {
  clearScoresHistogram,
  clearURLParams,
  extractBulk,
  extractRecipientDomains,
  extractSenderDomains,
  fetchActiveIntegrations,
  fetchCustomerRecipientsDomain,
  fetchCustomerUsersDomain,
  fetchFilterAttachmentModels,
  fetchFilterBodyModels,
  fetchFilterModels,
  fetchFiltersCounts,
  fetchManagementLevelsForFilter,
  fetchRecipientsForFilter,
  fetchRegionsForFilter,
  fetchReviewers,
  fetchSendersForFilter,
  fetchTagGroups,
  fetchTagsForFilter,
  fetchTeamsForFilter,
  reprocessBulk,
  setTree,
  setURLParams,
} from 'actions';
import { orderByNewParam } from 'actions/envelopeListView';
import { fetchIdentifiersForFilter } from 'actions/identifier';
import {
  clearRulePrompts,
  fetchFilterAttachmentRules,
  fetchFilterBodyRules,
  fetchFilterRules,
  fetchRuleContext,
  fetchRulePrompts,
} from 'actions/ruleGroup';
import EnvelopeListSidebarCollapsableSection from 'components/CollapsableSection/EnvelopeListSidebarCollapsableSection';
import CreateReviewSetHowTo from 'components/EnvelopeList/CreateReviewSetHowTo';
import ResourceFilter from 'components/Filters/ResourceFilter';
import DateFilter from 'components/Filters/SidebarTypeFilters/DateFilter';
import Permissions from 'components/Permissions';
import ThreeDotsMenu from 'components/ThreeDotsMenu';
import { GRAY_INFO_ICON } from 'constants/dashboardIcons';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import ExploreFiltersTreeContext, {
  ExploreFiltersTreeProvider,
} from 'contexts/ExploreFiltersTreeContext';
import useTeams from 'hooks/teams/useTeams';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getLowDataMode, getSurroundingContextRulesIds, getUser } from 'selectors/auth';
import { getActiveIntegrationsLoading } from 'selectors/campaigns';
import { eventsSidebarState } from 'selectors/envelopeListView';
import { getFiltersSearchString, getLanguages } from 'selectors/envelopes';
import {
  getKeyActionsNamesWithCounts,
  getPlatformCount,
  getPlatformNamesWithCounts,
} from 'selectors/events';
import {
  getFetchAllIdentifiersLoading,
  getIdentifiers,
  getIdentifiersTotalCount,
} from 'selectors/identifier';
import { getNavParamsByResourceMemo, getNavParamsFromTree } from 'selectors/nav';
import {
  getReviewHistoryLabelOptionsWithCounts,
  getReviewLabelOptionsWithCounts,
} from 'selectors/reviewStatus';
import { getRuleRevisions } from 'selectors/ruleGroups';
import { getTagsTotalCount } from 'selectors/tags';
import { getTeamsCount, getTeamsLoading, getTeamsWithCounts } from 'selectors/teams';
import {
  getCustomerRecipientsDomain,
  getCustomerRecipientsDomainCount,
  getCustomerRecipientsDomainLoading,
  getCustomerSenderDomain,
  getCustomerSenderDomainCount,
  getCustomerSenderDomainLoading,
  getReviewersWithCounts,
  getUsersLoading,
  getUsersNoSuperAdminList,
  getUsersTotalCount,
} from 'selectors/users';
import { useSelector } from 'store';
import logEvent from 'utils/analytics';
import { Operator } from 'utils/parserTree';
import { v4 } from 'uuid';
import AdvancedSearchModal from './AdvancedSearchModal';
import BackendResourceFilter from './BackendResourceFilter';
import BackendResourceFilterContext from './BackendResourceFilter/BackendResourceFilterContext';
import DaysOutOfRetentionFilter from './DaysOutOfRetention';
import ScoreFilter from './RiskScoreFilter/ScoreFilter';
import CategoriesFilter from './SidebarTypeFilters/CategoriesFilter/CategoriesFilter';
import MultiSelectFilter from './SidebarTypeFilters/MultiSelectFilter';
import TagsFilter from './SidebarTypeFilters/TagsFilter/TagsFilter';
import SortFilter from './SortFilter';

type ComponentProps = {
  resource: string;
};

const EventsFiltersSidebar: React.FC<ComponentProps> = ({ resource }) => {
  const dispatch = useDispatch();

  const { furtherReviewTagGroup, furtherReviewTagGroupId } = useSelector(eventsSidebarState);
  const [forceInfoModalOpen, setForceInfoModalOpen] = useState(0);
  const [isAdvancedSearchModalOpen, setIsAdvancedSearchModalOpen] = useState(false);

  const { data: teamsData } = useTeams({
    redirectOnError: false,
    kind: 'division',
  });
  const { data: regionsData } = useTeams({
    redirectOnError: false,
    kind: 'region',
  });
  const { data: managementLevelData } = useTeams({
    redirectOnError: false,
    kind: 'management-level',
  });

  const params = useSelector((state) => getNavParamsByResourceMemo(state, resource));
  const orderBy = params.order_by || '';

  const treeParams = useSelector(getNavParamsFromTree);
  const filtersSearchString = useSelector(getFiltersSearchString);

  const contextRules = useSelector(getSurroundingContextRulesIds);
  const lowDataMode = useSelector(getLowDataMode);

  const user = useSelector(getUser);
  const filteredParams = Object.entries(params).filter(
    ([key]) => !['offset', 'limit', 'order_desc', 'order_by', 'has_events'].includes(key)
  );

  const promptRule = treeParams?.rule_uuids?.[0] || '';

  // for score filter
  useEffect(() => {
    if (treeParams.rule_uuids && treeParams.rule_uuids.length === 1) {
      dispatch(fetchRulePrompts({ ruleGroupId: treeParams.rule_uuids[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, promptRule]);

  // reset score filter
  useEffect(() => {
    if (orderBy === 'llm_score' && (!treeParams.rule_uuids || treeParams.rule_uuids.length !== 1)) {
      dispatch(orderByNewParam(false));
      dispatch(clearScoresHistogram());
      dispatch(clearRulePrompts());
      dispatch(
        setURLParams({
          [`${resource}__order_by`]: 'create_at',
          [`${resource}__order_desc`]: 'true',
          [`${resource}__offset`]: '0',
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, promptRule, orderBy]);

  useEffect(() => {
    if (furtherReviewTagGroupId) {
      dispatch(
        fetchTagGroups({
          uuids: [furtherReviewTagGroupId],
        })
      );
    }
  }, [dispatch, furtherReviewTagGroupId]);

  useEffect(() => {
    dispatch(fetchFiltersCounts({}));
  }, [dispatch, filtersSearchString]);

  const handleResetFiltersClick = (): void => {
    logEvent('envelopes-list-clear-filters');
    dispatch(setTree({ tree: { op: Operator.AND, data: [], id: v4() } }));
    dispatch(clearURLParams());
    dispatch(orderByNewParam(false));
  };

  const handleReprocessStarshipClick = (): void => {
    dispatch(reprocessBulk({ starship: true }));
  };

  const handleReprocessNewDocClick = (): void => {
    dispatch(reprocessBulk({ starship: true, regenerate: true }));
  };

  const handleExtractClick = (): void => {
    dispatch(extractBulk({ starship: true }));
  };

  const handleExtractSenderDomains = (): void => {
    dispatch(extractSenderDomains);
  };

  const handleExtractRecipientDomains = (): void => {
    dispatch(extractRecipientDomains);
  };

  const toggleAdvancedSearchModalOpen = (): void => {
    setIsAdvancedSearchModalOpen(!isAdvancedSearchModalOpen);
  };

  const handleOpenInfo = (): void => {
    setForceInfoModalOpen((i) => i + 1);
  };

  const options = [
    {
      id: 'advanced-query',
      label: 'Advanced Query',
      action: toggleAdvancedSearchModalOpen,
      permissions: 'communications.advanced',
    },
    {
      id: 'reprocess-starship',
      label: 'Reprocess Starship',
      action: handleReprocessStarshipClick,
      permissions: 'communications.reprocess',
    },
    {
      id: 'reprocess-starship',
      label: 'Reprocess New Document',
      action: handleReprocessNewDocClick,
      permissions: 'communications.reprocess',
    },
    {
      id: 'Extract-entites',
      label: 'Extract Entities',
      action: handleExtractClick,
      // TODO: more perms
      permissions: 'communications.reprocess',
    },
    {
      id: 'Extract-sender-domains',
      label: 'Sender Domains',
      action: handleExtractSenderDomains,
      permissions: 'communications.reprocess',
    },
    {
      id: 'Extract-recipient-domains',
      label: 'Recipient Domains',
      action: handleExtractRecipientDomains,
      permissions: 'communications.reprocess',
    },
  ];

  return (
    <ExploreFiltersTreeProvider>
      <div className="flex flex-col gap-4">
        <div id="envelope-header-div" className="bg-litlingo-primary-140">
          <div className="items-center flex flex-row justify-between">
            <div className="flex flex-row justify-start gap-2">
              <div className="flex flex-row items-baseline text-heading-2">
                <span className="text-white">Filters</span>
              </div>

              <Permissions action="communications.reprocess">
                <div className="flex flex-col justify-center items-end">
                  <ThreeDotsMenu options={options} colorClass="text-white" translate />
                </div>
              </Permissions>
            </div>
            <button
              type="button"
              className={`underline focus:outline-none  ${
                !(filteredParams.length > 0) && 'text-litlingo-gray-4 cursor-default'
              }`}
              onClick={handleResetFiltersClick}
              disabled={!(filteredParams.length > 0)}
            >
              Clear All
            </button>
          </div>
        </div>
        <div className="flex flex-col relative z-20">
          <div>
            <div className="pt-2 pb-4">
              <SortFilter resource={resourceQueryParamName.envelopes} />
              {orderBy === 'llm_score' && <ScoreFilter />}
            </div>
            <EnvelopeListSidebarCollapsableSection
              headerTitle="Date"
              isDefaultClosed
              selectedFilters={
                (params && (params.created_before || params.created_after)) ||
                (treeParams && treeParams.date_range)
              }
            >
              <div className="flex flex-col gap-2">
                <DateFilter resource={resource} />
              </div>
            </EnvelopeListSidebarCollapsableSection>

            <EnvelopeListSidebarCollapsableSection
              headerTitle="Models"
              isDefaultClosed
              selectedFilters={
                treeParams.campaign_uuids ||
                treeParams.campaign_uuids_and ||
                treeParams.not_campaign_uuids ||
                treeParams.not_campaign_uuids_and ||
                treeParams.rule_uuids ||
                treeParams.rule_uuids_and ||
                treeParams.not_rule_uuids ||
                treeParams.not_rule_uuids_and ||
                treeParams.body_campaign_uuids ||
                treeParams.not_body_campaign_uuids ||
                treeParams.body_rule_uuids ||
                treeParams.not_body_rule_uuids ||
                treeParams.attachment_campaign_uuids ||
                treeParams.not_attachment_campaign_uuids ||
                treeParams.attachment_rule_uuids ||
                treeParams.not_attachment_rule_uuids ||
                treeParams.annotator_uuids ||
                treeParams.not_annotator_uuids
              }
            >
              <div className="flex flex-col gap-y-3 mt-2">
                <Permissions action="communication_envelopes.filter_categories">
                  <div>
                    <div>Models</div>
                    <CategoriesFilter
                      campaignsKey="campaign_uuids"
                      rulesKey="rule_uuids"
                      title="Models"
                      fetchCampaigns={fetchFilterModels}
                      fetchRules={fetchFilterRules}
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="communication_envelopes.list">
                  <div>
                    <div>Models in Body</div>
                    <CategoriesFilter
                      campaignsKey="body_campaign_uuids"
                      rulesKey="body_rule_uuids"
                      title="Models"
                      fetchCampaigns={fetchFilterBodyModels}
                      fetchRules={fetchFilterBodyRules}
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="communication_envelopes.list">
                  <div>
                    <div>Models in Attachment</div>
                    <CategoriesFilter
                      campaignsKey="attachment_campaign_uuids"
                      rulesKey="attachment_rule_uuids"
                      title="Models"
                      fetchCampaigns={fetchFilterAttachmentModels}
                      fetchRules={fetchFilterAttachmentRules}
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="communication_envelopes.filter_annotators">
                  <div>
                    <div>Identifiers</div>
                    <BackendResourceFilter
                      resource={resource}
                      fetchAction={fetchIdentifiersForFilter}
                      getResourceList={getIdentifiers}
                      getResourceCount={getIdentifiersTotalCount}
                      getLoading={getFetchAllIdentifiersLoading}
                      filterKey="annotator_uuids"
                      title="Identifiers"
                      nameKey="name"
                      dataTestid="identifiers-filter-button"
                      allowExclude
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>
              </div>
            </EnvelopeListSidebarCollapsableSection>

            <EnvelopeListSidebarCollapsableSection
              headerTitle="Message Properties"
              isDefaultClosed
              selectedFilters={
                treeParams.recipients ||
                treeParams.not_recipients ||
                treeParams.sender_team_uuids_and ||
                treeParams.sender_team_uuids ||
                treeParams.not_sender_team_uuids ||
                treeParams.sender_regions ||
                treeParams.not_sender_regions ||
                treeParams.sender_management_levels ||
                treeParams.not_sender_management_levels ||
                treeParams.platforms ||
                treeParams.not_platforms ||
                treeParams.has_attachments ||
                treeParams.inbound ||
                treeParams.has_translation ||
                treeParams.created_by ||
                treeParams.not_created_by ||
                treeParams.sender_domain ||
                treeParams.not_sender_domain ||
                treeParams.recipient_domains ||
                treeParams.not_recipient_domains ||
                treeParams.rule_context_uuids ||
                treeParams.rule_context_uuids_and ||
                treeParams.not_rule_context_uuids ||
                treeParams.not_rule_context_uuids_and ||
                treeParams.language ||
                treeParams.not_language
              }
            >
              <div className="flex flex-col gap-y-3 mt-2">
                {contextRules.length > 0 && (
                  <Permissions action="users.list">
                    <div>
                      <div>Context</div>
                      <BackendResourceFilterContext
                        resource={resource}
                        fetchAction={fetchRuleContext}
                        getResourceList={getRuleRevisions}
                        uuids={contextRules}
                        getLoading={getUsersLoading}
                        filterKey="rule_context_uuids"
                        title="Context"
                        nameKey="name"
                        dataTestid="context-filter-button"
                        allowExclude
                        treeContext={ExploreFiltersTreeContext}
                      />
                    </div>
                  </Permissions>
                )}
                {!lowDataMode && (
                  <Permissions action="users.list">
                    <div>
                      <div>Sender</div>
                      <BackendResourceFilter
                        resource={resource}
                        fetchAction={fetchSendersForFilter}
                        getResourceList={getUsersNoSuperAdminList}
                        getResourceCount={getUsersTotalCount}
                        getLoading={getUsersLoading}
                        filterKey="created_by"
                        title="Sender"
                        nameKey="name"
                        secondNameKey="email"
                        dataTestid="user-filter-button"
                        allowExclude
                        treeContext={ExploreFiltersTreeContext}
                      />
                    </div>
                  </Permissions>
                )}
                {!lowDataMode && (
                  <Permissions action="users.list">
                    <div>
                      <div>Recipient</div>
                      <BackendResourceFilter
                        resource={resource}
                        fetchAction={fetchRecipientsForFilter}
                        getResourceList={getUsersNoSuperAdminList}
                        getResourceCount={getUsersTotalCount}
                        getLoading={getUsersLoading}
                        filterKey="recipients"
                        title="Recipients"
                        nameKey="name"
                        itemField="email"
                        secondNameKey="email"
                        dataTestid="user-filter-button"
                        allowExclude
                        treeContext={ExploreFiltersTreeContext}
                      />
                    </div>
                  </Permissions>
                )}
                <Permissions action="users.domains">
                  <div>
                    <div>Sender Domains</div>
                    <BackendResourceFilter
                      resource={resource}
                      fetchAction={fetchCustomerUsersDomain}
                      getResourceList={getCustomerSenderDomain}
                      getResourceCount={getCustomerSenderDomainCount}
                      getLoading={getCustomerSenderDomainLoading}
                      filterKey="sender_domain"
                      title="Sender Domains"
                      nameKey="name"
                      dataTestid="user-filter-button"
                      allowExclude
                      notTreeLabel
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="users.domains">
                  <div>
                    <div>Recipients Domains</div>
                    <BackendResourceFilter
                      resource={resource}
                      fetchAction={fetchCustomerRecipientsDomain}
                      getResourceList={getCustomerRecipientsDomain}
                      getResourceCount={getCustomerRecipientsDomainCount}
                      getLoading={getCustomerRecipientsDomainLoading}
                      filterKey="recipient_domains"
                      title="Recipient Domains"
                      nameKey="name"
                      dataTestid="user-filter-button"
                      allowExclude
                      notTreeLabel
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                {teamsData?.count && teamsData.count > 0 ? (
                  <Permissions action="communication_envelopes.filter_division">
                    <div>
                      <div>Division</div>
                      <BackendResourceFilter
                        resource={resource}
                        fetchAction={fetchTeamsForFilter}
                        getResourceCount={getTeamsCount}
                        getResourceList={getTeamsWithCounts}
                        getLoading={getTeamsLoading}
                        filterKey="sender_team_uuids"
                        title="Division"
                        nameKey="name"
                        filterCountsField="teams"
                        allowExclude
                        treeContext={ExploreFiltersTreeContext}
                      />
                    </div>
                  </Permissions>
                ) : null}
                {regionsData?.count && regionsData.count > 0 ? (
                  <Permissions action="communication_envelopes.filter_division">
                    <div>
                      <div>Region</div>
                      <BackendResourceFilter
                        resource={resource}
                        fetchAction={fetchRegionsForFilter}
                        getResourceCount={getTeamsCount}
                        getResourceList={getTeamsWithCounts}
                        getLoading={getTeamsLoading}
                        filterKey="sender_regions"
                        title="Region"
                        nameKey="name"
                        allowExclude
                        filterCountsField="teams"
                        treeContext={ExploreFiltersTreeContext}
                      />
                    </div>
                  </Permissions>
                ) : null}
                {managementLevelData?.count && managementLevelData.count > 0 ? (
                  <Permissions action="communication_envelopes.filter_division">
                    <div>
                      <div>Management Level</div>
                      <BackendResourceFilter
                        resource={resource}
                        fetchAction={fetchManagementLevelsForFilter}
                        getResourceCount={getTeamsCount}
                        getResourceList={getTeamsWithCounts}
                        getLoading={getTeamsLoading}
                        filterKey="sender_management_levels"
                        title="Management Level"
                        nameKey="name"
                        allowExclude
                        filterCountsField="teams"
                        treeContext={ExploreFiltersTreeContext}
                      />
                    </div>
                  </Permissions>
                ) : null}
                <Permissions action="communication_envelopes.list">
                  <div>
                    <div>Language</div>
                    <ResourceFilter
                      getResourceList={getLanguages}
                      filterKey="language"
                      title="Language"
                      nameKey="label"
                      sliceSize={1000}
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="communication_envelopes.filter_direction">
                  <div>
                    <div>Direction</div>
                    <MultiSelectFilter
                      options={{
                        true: 'Inbound',
                        false: 'Outbound',
                      }}
                      filter="inbound"
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="communication_envelopes.filter_source">
                  <div>
                    <div>Platform</div>
                    <BackendResourceFilter
                      resource={resource}
                      getResourceList={getPlatformNamesWithCounts}
                      fetchAction={fetchActiveIntegrations}
                      getResourceCount={getPlatformCount}
                      getLoading={getActiveIntegrationsLoading}
                      filterKey="platforms"
                      title="Platform"
                      nameKey="name"
                      dataTestid="platform-filter-button"
                      allowExclude
                      notTreeLabel
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>

                <div>
                  <div>Attachments</div>
                  <MultiSelectFilter
                    options={{
                      true: 'Has Attachments',
                      false: 'No Attachments',
                    }}
                    filter="has_attachments"
                    treeContext={ExploreFiltersTreeContext}
                  />
                </div>
                <Permissions action="communication_envelopes.filter_same_sentence">
                  <div>
                    <div>Translations</div>
                    <MultiSelectFilter
                      options={{
                        true: 'Has Translations',
                        false: 'No Translations',
                      }}
                      filter="has_translation"
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                {(user.customer?.config.communication_retention_days ||
                  user.customer?.config.communication_retention_days === 0) && (
                  <div>
                    <div>Retention Window</div>
                    <DaysOutOfRetentionFilter
                      resource={resource}
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                )}
              </div>
            </EnvelopeListSidebarCollapsableSection>

            <EnvelopeListSidebarCollapsableSection
              headerTitle="Reviewer Actions"
              isDefaultClosed
              selectedFilters={
                treeParams.tags ||
                treeParams.not_tags ||
                treeParams.tag_value_groups ||
                treeParams.not_tag_value_groups ||
                treeParams.review_values ||
                treeParams.not_review_values ||
                treeParams.review_values ||
                treeParams.not_review_values ||
                treeParams.reviewed_by_user_uuids ||
                treeParams.not_reviewed_by_user_uuids ||
                treeParams.has_comments ||
                furtherReviewTagGroup?.tag_values
                  .filter((t) => params.tags?.includes(t.uuid))
                  .map((tag) => tag.uuid)
              }
            >
              <div className="flex flex-col gap-y-3 mt-2">
                <Permissions action="communication_envelopes.filter_status">
                  <div>
                    <div>Status</div>
                    <ResourceFilter
                      getResourceList={getReviewLabelOptionsWithCounts}
                      filterKey="review_values"
                      title="Status"
                      nameKey="name"
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                  <div>
                    <div>Review History</div>
                    <ResourceFilter
                      getResourceList={getReviewHistoryLabelOptionsWithCounts}
                      filterKey="review_history"
                      title="Review History"
                      nameKey="name"
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>

                <div>
                  <div>Tags</div>
                  <TagsFilter
                    title="Tags"
                    fetchActionTag={fetchTagsForFilter}
                    getTagCount={getTagsTotalCount}
                    treeContext={ExploreFiltersTreeContext}
                  />
                </div>
                <Permissions action="users.list">
                  <div>
                    <div>Reviewer</div>
                    <BackendResourceFilter
                      resource={resource}
                      fetchAction={fetchReviewers}
                      getResourceList={getReviewersWithCounts}
                      getResourceCount={getUsersTotalCount}
                      getLoading={getUsersLoading}
                      filterKey="reviewed_by_user_uuids"
                      title="Reviewer"
                      nameKey="name"
                      dataTestid="reviewer-filter-button"
                      allowExclude
                      filterCountsField="reviewers"
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                {furtherReviewTagGroup && furtherReviewTagGroup.tag_values.length > 0 && (
                  <div>
                    <div>Workflow Stage</div>

                    <MultiSelectFilter
                      options={furtherReviewTagGroup.tag_values.reduce<Record<string, string>>(
                        (acc, tag) => {
                          acc[tag.uuid] = tag.value;
                          return acc;
                        },
                        {}
                      )}
                      filter="tags"
                      notFilter="not_tags"
                      allowMultipleValues
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                )}
                <div>
                  <div>Comments</div>
                  <MultiSelectFilter
                    options={{
                      true: 'Has Comments',
                      false: 'No Comments',
                    }}
                    filter="has_comments"
                    treeContext={ExploreFiltersTreeContext}
                  />
                </div>
              </div>
            </EnvelopeListSidebarCollapsableSection>
            <EnvelopeListSidebarCollapsableSection
              headerTitle="Product"
              selectedFilters={
                treeParams.integration_types ||
                treeParams.key_actions ||
                treeParams.not_key_actions ||
                treeParams.key_actions_and ||
                treeParams.not_key_actions_and
              }
              isDefaultClosed
            >
              <div className="flex flex-col gap-y-3 mt-2">
                <Permissions action="communication_envelopes.list">
                  <div>
                    <div>Type</div>
                    <MultiSelectFilter
                      options={{
                        api: 'Insight',
                        app: 'Prevent',
                      }}
                      filter="integration_types"
                      allowMultipleValues
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>
                <Permissions action="communication_envelopes.filter_actions">
                  <div>
                    <div>End User Actions</div>
                    <ResourceFilter
                      getResourceList={getKeyActionsNamesWithCounts}
                      filterKey="key_actions"
                      title="Actions"
                      nameKey="name"
                      treeContext={ExploreFiltersTreeContext}
                    />
                  </div>
                </Permissions>
              </div>
            </EnvelopeListSidebarCollapsableSection>
            <div className="flex w-full justify-center mt-8">
              <button
                type="button"
                onClick={handleOpenInfo}
                className="flex flex-row items-center gap-2 focus:outline-none"
              >
                {GRAY_INFO_ICON}{' '}
                <span className="text-litlingo-white underline">More information</span>
              </button>
            </div>
          </div>
        </div>

        <CreateReviewSetHowTo forceOpen={forceInfoModalOpen} />
        {isAdvancedSearchModalOpen && (
          <AdvancedSearchModal
            isModalOpen={isAdvancedSearchModalOpen}
            toggleModalOpen={toggleAdvancedSearchModalOpen}
          />
        )}
      </div>
    </ExploreFiltersTreeProvider>
  );
};

export default EventsFiltersSidebar;
